import { errorMsg } from '../../utils/errorMsg';
import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Button, TextField, FormControl, InputLabel,
  Select, MenuItem, FormControlLabel, Checkbox, CircularProgress, Alert,
  Paper, Grid
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { useNavigate, useParams } from 'react-router-dom';
import { criarCurso, atualizarCurso, getCurso, listarMinisterios } from '../../api/cursos';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ContextNavBar from '../../components/ContextNavBar';
import ModuleHeader from '../../components/ModuleHeader';

function CursoForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const cursoId = id || null;
  const isEditing = !!cursoId;

  const [ministerios, setMinisterios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState('');
  const [form, setForm] = useState({
    titulo: '',
    descricao: '',
    ministerio_id: '',
    foto_capa: '',
    data_inicio: '',
    data_fim: '',
    tipo: 'sazonal',
    vagas_maximas: '',
    carga_horaria_minima: '',
    permitir_inscricao_externo: false,
    local: ''
  });

  useEffect(() => {
    setLoading(true);
    Promise.all([
      listarMinisterios(),
      isEditing ? getCurso(cursoId) : Promise.resolve(null)
    ])
      .then(([ministeriosData, cursoData]) => {
        setMinisterios(ministeriosData);
        if (cursoData) {
          setForm({
            titulo: cursoData.titulo || '',
            descricao: cursoData.descricao || '',
            ministerio_id: cursoData.ministerio_id || '',
            foto_capa: cursoData.foto_capa || '',
            data_inicio: cursoData.data_inicio || '',
            data_fim: cursoData.data_fim || '',
            tipo: cursoData.tipo || 'sazonal',
            vagas_maximas: cursoData.vagas_maximas != null ? String(cursoData.vagas_maximas) : '',
            carga_horaria_minima: cursoData.carga_horaria_minima != null ? String(cursoData.carga_horaria_minima) : '',
            permitir_inscricao_externo: cursoData.permitir_inscricao_externo || false,
            local: cursoData.local || ''
          });
        }
      })
      .catch((err) => {
        setErro(errorMsg(err) || 'Erro ao carregar dados');
      })
      .finally(() => setLoading(false));
  }, [cursoId, isEditing]);

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [field]: value }));
  };

  const handleSalvar = async () => {
    if (!form.titulo.trim()) { setErro('Título é obrigatório.'); return; }
    setSaving(true);
    setErro('');
    try {
      const payload = {
        ...form,
        vagas_maximas: form.vagas_maximas ? parseInt(form.vagas_maximas, 10) : null,
        carga_horaria_minima: form.carga_horaria_minima ? parseInt(form.carga_horaria_minima, 10) : null,
        data_inicio: form.data_inicio || null,
        data_fim: form.data_fim || null,
        ministerio_id: form.ministerio_id ? parseInt(form.ministerio_id, 10) : null
      };
      let curso;
      if (isEditing) {
        curso = await atualizarCurso(cursoId, payload);
      } else {
        curso = await criarCurso(payload);
      }
      navigate(`/cursos/${curso.id}`);
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao salvar curso');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <>
        <Header />
        <ContextNavBar context="cursos" />
        <Container maxWidth="md" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        </Container>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <ContextNavBar context="cursos" />
      <Container maxWidth="md" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/cursos')} size="small">Voltar</Button>
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 3, fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
        {isEditing ? 'Editar Curso' : 'Novo Curso'}
      </Typography>

      <Paper sx={{ p: 3, borderRadius: 2 }} elevation={2}>
        {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField label="Título *" value={form.titulo} onChange={handleChange('titulo')} fullWidth />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Descrição" value={form.descricao} onChange={handleChange('descricao')} fullWidth multiline rows={3} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Ministério</InputLabel>
              <Select value={form.ministerio_id} label="Ministério" onChange={handleChange('ministerio_id')}>
                <MenuItem value="">Nenhum</MenuItem>
                {ministerios.map(m => (
                  <MenuItem key={m.id} value={m.id}>{m.nome}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="URL da Foto de Capa" value={form.foto_capa} onChange={handleChange('foto_capa')} fullWidth />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Data de Início" type="date" value={form.data_inicio} onChange={handleChange('data_inicio')} fullWidth InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Data de Fim" type="date" value={form.data_fim} onChange={handleChange('data_fim')} fullWidth InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select value={form.tipo} label="Tipo" onChange={handleChange('tipo')}>
                <MenuItem value="sazonal">Sazonal</MenuItem>
                <MenuItem value="avulso">Avulso</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Vagas Máximas" type="number" value={form.vagas_maximas} onChange={handleChange('vagas_maximas')} fullWidth inputProps={{ min: 0 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Carga horária mínima (minutos)" type="number" value={form.carga_horaria_minima || ''} onChange={handleChange('carga_horaria_minima')} fullWidth helperText="Mínimo de minutos com presença para emitir certificado" inputProps={{ min: 0 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Local" value={form.local} onChange={handleChange('local')} fullWidth />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Checkbox checked={form.permitir_inscricao_externo} onChange={handleChange('permitir_inscricao_externo')} />}
              label="Permitir inscrição de externos"
            />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={() => navigate('/cursos')}>Cancelar</Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSalvar} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : (isEditing ? 'Atualizar' : 'Salvar')}
          </Button>
        </Box>
      </Paper>
    </Container>
      <Footer />
    </>
  );
}

export default CursoForm;
